import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Tooltip } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import API from '../../services/api';

export type ProcessingStatus =
  | 'pending'
  | 'processing'
  | 'parsed'
  | 'chunked'
  | 'indexed'
  | 'completed'
  | 'failed';

interface MaterialStatusProps {
  materialId: string;
  /**
   * Polling interval in milliseconds. Defaults to 1000 ms
   */
  pollIntervalMs?: number;
  /**
   * Array of statuses that should stop polling once reached. Defaults to ['completed', 'failed']
   */
  stopOnStatuses?: ProcessingStatus[];
  /**
   * Optional callback whenever status is updated
   */
  onStatusChange?: (status: ProcessingStatus) => void;
  /** Optional sx applied to wrapper box */
  sx?: Record<string, any>;
}

function getStatusIcon(status: ProcessingStatus, size: number = 16) {
  const iconProps = { sx: { fontSize: size } } as const;
  switch (status) {
    case 'pending':
      return <HourglassEmptyIcon {...iconProps} color="action" />;
    case 'processing':
    case 'parsed':
      return <TextSnippetIcon {...iconProps} color="primary" />;
    case 'chunked':
    case 'indexed':
      return <SyncIcon {...iconProps} color="primary" className="material-status-rotating" />;
    case 'completed':
      return <CheckCircleIcon {...iconProps} color="success" />;
    case 'failed':
      return <ErrorOutlineIcon {...iconProps} color="error" />;
    default:
      return null;
  }
}

function MaterialStatus(props: MaterialStatusProps) {
  const {
    materialId,
    pollIntervalMs = 1000,
    stopOnStatuses = ['completed', 'failed'],
    onStatusChange,
    sx = {},
  } = props;

  const [status, setStatus] = useState<ProcessingStatus>('pending');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    async function fetchStatus() {
      try {
        const { data } = await API().get<{ status: ProcessingStatus }>(
          `/materials/${materialId}/status`
        );
        if (data.status && data.status !== status) {
          setStatus(data.status);
          onStatusChange?.(data.status);
        }
        if (
          stopOnStatuses.includes(data.status) ||
          data.status === 'completed' ||
          data.status === 'failed'
        ) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      } catch (error) {
        console.error('Failed to fetch material status', error);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    }

    // initial fetch immediately
    fetchStatus();
    timerRef.current = setInterval(fetchStatus, pollIntervalMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialId]);

  const showSpinner = timerRef.current !== null;

  return (
    <Tooltip title={status} arrow>
      <Stack direction="row" alignItems="center" justifyContent="center" width="24px" height="24px">
        {getStatusIcon(status)}
        {showSpinner && (
          <CircularProgress
            size={24}
            thickness={4}
            sx={{
              color: 'primary.main',
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 2,
            }}
          />
        )}
      </Stack>
    </Tooltip>
  );
}

export default MaterialStatus;
